import { api } from './api';

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role: string;
  is_active: boolean;
  last_login?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  invite_pending?: boolean;
  invite_expires_at?: string | null;
  mfa_enabled?: boolean;
  mfa_enrolled_at?: string | null;
}

export interface MfaStatusResponse {
  mfa: {
    enabled: boolean;
    pending_setup: boolean;
    enrolled_at?: string | null;
    last_verified_at?: string | null;
  };
}

export interface MfaSetupResponse extends MfaStatusResponse {
  setup: {
    secret: string;
    provisioning_uri: string;
  };
}

export interface InviteResponse {
  user: AdminUser;
  invite: {
    token_type: string;
    expires_at: string;
    activation_url?: string | null;
  };
  delivery?: {
    enabled: boolean;
    sent: boolean;
    to: string;
    error?: string | null;
  };
}

export interface InviteStatusResponse {
  ok: boolean;
  user: {
    username: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
  };
  invite: {
    expires_at: string;
  };
  password_policy?: {
    min_length: number;
    require_upper: boolean;
    require_lower: boolean;
    require_digit: boolean;
    require_symbol: boolean;
  };
}

export interface PasswordResetStatusResponse {
  ok: boolean;
  user: {
    username: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
  };
  reset: {
    expires_at: string;
  };
  password_policy?: {
    min_length: number;
    require_upper: boolean;
    require_lower: boolean;
    require_digit: boolean;
    require_symbol: boolean;
  };
}

export interface UserListFilters {
  q?: string;
  active?: boolean | 'all';
}

export async function listAdminUsers(filters: UserListFilters = {}): Promise<AdminUser[]> {
  const params: Record<string, string> = {};
  if (filters.q) params.q = filters.q;
  if (filters.active === true) params.active = 'true';
  if (filters.active === false) params.active = 'false';
  const response = await api.get('/api/v1/auth/admin/users', { params });
  return Array.isArray(response.data?.users) ? response.data.users : [];
}

export async function createAdminUser(payload: {
  username: string;
  email: string;
  password: string;
  role: string;
  first_name?: string;
  last_name?: string;
  is_active?: boolean;
}): Promise<AdminUser> {
  const response = await api.post('/api/v1/auth/admin/users', payload);
  return response.data.user;
}

export async function inviteAdminUser(payload: {
  username: string;
  email: string;
  role: string;
  first_name?: string;
  last_name?: string;
  expires_in_hours?: number;
}): Promise<InviteResponse> {
  const response = await api.post('/api/v1/auth/admin/users/invite', payload);
  return response.data;
}

export async function updateAdminUser(
  userId: string,
  payload: Partial<Pick<AdminUser, 'username' | 'email' | 'first_name' | 'last_name' | 'role' | 'is_active'>>
): Promise<AdminUser> {
  const response = await api.patch(`/api/v1/auth/admin/users/${userId}`, payload);
  return response.data.user;
}

export async function resetAdminUserPassword(userId: string, password: string): Promise<void> {
  await api.post(`/api/v1/auth/admin/users/${userId}/reset_password`, { password });
}

export async function reissueAdminInvite(userId: string, expires_in_hours?: number): Promise<InviteResponse> {
  const response = await api.post(`/api/v1/auth/admin/users/${userId}/invite`, {
    ...(expires_in_hours ? { expires_in_hours } : {}),
  });
  return response.data;
}

export async function fetchInviteStatus(token: string): Promise<InviteStatusResponse> {
  const response = await api.get('/api/v1/auth/invite/status', { params: { token } });
  return response.data;
}

export async function activateInvitedUser(payload: {
  token: string;
  password: string;
  first_name?: string;
  last_name?: string;
}): Promise<AdminUser> {
  const response = await api.post('/api/v1/auth/activate', payload);
  return response.data.user;
}

export async function changeOwnPassword(payload: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  await api.post('/api/v1/auth/change_password', payload);
}

export async function requestPasswordReset(payload: {
  identifier: string;
}): Promise<{ ok: boolean; message: string }> {
  const response = await api.post('/api/v1/auth/forgot_password', payload);
  return response.data;
}

export async function fetchPasswordResetStatus(token: string): Promise<PasswordResetStatusResponse> {
  const response = await api.get('/api/v1/auth/reset/status', { params: { token } });
  return response.data;
}

export async function resetPasswordWithToken(payload: {
  token: string;
  password: string;
}): Promise<AdminUser> {
  const response = await api.post('/api/v1/auth/reset_password', payload);
  return response.data.user;
}

export async function fetchOwnMfaStatus(): Promise<MfaStatusResponse> {
  const response = await api.get('/api/v1/auth/mfa');
  return response.data;
}

export async function startOwnMfaSetup(payload: {
  current_password: string;
}): Promise<MfaSetupResponse> {
  const response = await api.post('/api/v1/auth/mfa/setup', payload);
  return response.data;
}

export async function verifyOwnMfaSetup(payload: {
  otp_code: string;
}): Promise<MfaStatusResponse> {
  const response = await api.post('/api/v1/auth/mfa/verify', payload);
  return response.data;
}

export async function disableOwnMfa(payload: {
  current_password: string;
  otp_code: string;
}): Promise<MfaStatusResponse> {
  const response = await api.post('/api/v1/auth/mfa/disable', payload);
  return response.data;
}
